import React, {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { Filtros } from '@toctoc/design-system';
import { IEstructuraFiltros } from './Filters.interface';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setFilter, selectFilter } from '@/app/reducers/filters';
import { filterDataGet } from '@/app/reducers/filtersData';
import { setStatusToIdle } from '@/app/reducers/properties';
import { IFiltros } from '@/components/templates/search/interfaces/IEstructuraPrincipal';
import {
	selectFiltersAvailable,
	setFiltersAvailable,
} from '@/app/reducers/filtersAvailable';
import { constructUrlFromFilters, matchQueryWithValueList, filterVisibleFilters, cleanIncompatibleFilters } from './util';
import { useRouter } from 'next/router';
import { getUrlParameters } from '@/utils/url';
import { isEmpty } from '@/utils/object';

type FiltersSetterType = Dispatch<SetStateAction<IFiltros[]>>;
// eslint-disable-next-line no-unused-vars
type FiltersSetterFunctionType = (prevState: IFiltros[]) => IFiltros[];

export const Filters = ({
	idsRequeridos,
	isMobile,
	filtrosBase,
	triggerSearch,
	urlSlug,
	pathListaSeo
}: IEstructuraFiltros) => {
	const dispatch = useAppDispatch();

	const filtersAvailableData = useAppSelector(selectFiltersAvailable).filtros;
	const filterDataState = useAppSelector((state) => state.FilterDataState);
	const filterState = useAppSelector(selectFilter);
	const router = useRouter();
	//const { pathListaSeo } = router.query;
	const [tof, tpf, region, comuna] = (pathListaSeo as string[]) ?? [];
	const dataAttr = { 'data-apartado': `${tof} ${tpf}` };
	const [filtersSelected, setFiltersSelected] = useState(filterState.filtros);
	const [hardUrlSegmentToFollow, setHardUrlSegmentToFollow] = useState([tof]);

	const setFiltersAvailableData: FiltersSetterType = (prevUpdate) => {
		const newState =
			typeof prevUpdate === 'function'
				? (prevUpdate as FiltersSetterFunctionType)(filtersAvailableData)
				: prevUpdate;
		dispatch(setFiltersAvailable({ filtros: newState }));
	};

	// Enhanced wrapper function that cleans incompatible filters
	const setFiltersSelectedWithCleaning = useCallback((newFiltersOrFunction: React.SetStateAction<IFiltros[]>) => {
		// Handle both direct values and function updates
		setFiltersSelected(currentFilters => {
			const newFilters = typeof newFiltersOrFunction === 'function'
				? newFiltersOrFunction(currentFilters)
				: newFiltersOrFunction;

			// Clean filters that are not compatible according to idsRequeridos
			return cleanIncompatibleFilters(newFilters, idsRequeridos);
		});
	}, [idsRequeridos]);

	// Wrapper function to detect user interactions vs automatic parsing
	const setFiltersSelectedFromUser = useCallback((newFilters: IFiltros[]) => {
		// console.log('👤 [setFiltersSelectedFromUser] User interaction detected');
		userHasInteracted.current = true; // Mark that user has interacted
		const cleanedFilters = cleanIncompatibleFilters(newFilters, idsRequeridos);
		setFiltersSelected(cleanedFilters);
	}, [idsRequeridos]);

	const setFiltersSelectedFromSystem = useCallback((newFilters: IFiltros[]) => {
		// console.log('🤖 [setFiltersSelectedFromSystem] System/automatic change');
		// Don't mark as user interaction
		const cleanedFilters = cleanIncompatibleFilters(newFilters, idsRequeridos);
		setFiltersSelected(cleanedFilters);
	}, [idsRequeridos]);

	const fromRouterPush = useRef(false);
	const queryParsed = useRef(false);
	const componentMounted = useRef(false);
	const isFirstLoad = useRef(true);
	const userHasInteracted = useRef(false); // Track real user interactions
	const windowUrlQuery = useRef<{ [key: string]: string | string[] }>({});

	const setFromPushToFalse = useCallback(() => {
		const [url] = constructUrlFromFilters(filterState.filtros, urlSlug) ?? [];
		const [newParameterLessUrl] = url.split('?');

		const [, filter_tof] = newParameterLessUrl.split('/');
		const [firstSegment] = hardUrlSegmentToFollow;
		if (
			queryParsed.current === true &&
			componentMounted.current &&
			filter_tof !== firstSegment
		)
			fromRouterPush.current = false;
	}, [filterState.filtros, hardUrlSegmentToFollow, urlSlug]);

	const componentMountEffect = useCallback(() => {
		componentMounted.current = true;
		windowUrlQuery.current = getUrlParameters();
	}, []);

	// Update windowUrlQuery when router query changes
	const routerQueryChangeEffect = useCallback(() => {
		// console.log("🔄 [routerQueryChangeEffect] Starting:", JSON.stringify({
		// 	routerAsPath: router.asPath,
		// 	queryParsed: queryParsed.current,
		// 	componentMounted: componentMounted.current,
		// 	windowUrlQuery: windowUrlQuery.current
		// }, null, 2));

		// Skip if we haven't parsed query yet or component not mounted
		if (queryParsed.current === false || !componentMounted.current) {
			// console.log("⏭️ [routerQueryChangeEffect] Skipping - not parsed or not mounted");
			return;
		}

		// Get current query parameters from URL
		const currentQueryParams = getUrlParameters();
		// console.log("📋 [routerQueryChangeEffect] Current query params:", JSON.stringify(currentQueryParams, null, 2));

		// Check if query parameters have changed
		const queryParamsChanged =
			JSON.stringify(windowUrlQuery.current) !==
			JSON.stringify(currentQueryParams);

		if (!queryParamsChanged) {
			// console.log("✅ [routerQueryChangeEffect] Query params unchanged");
			return;
		}

		// console.log("🔄 [routerQueryChangeEffect] Query params changed:", JSON.stringify({
		// 	before: windowUrlQuery.current,
		// 	after: currentQueryParams
		// }, null, 2));

		// Update the stored query params
		windowUrlQuery.current = currentQueryParams;

		// Process query parameters and convert to filters
		const queryEntries = Object.entries(currentQueryParams);
		const queryFilters: IFiltros[] = [];

		if (Object.keys(currentQueryParams).length > 0) {
			// console.log("🔍 [routerQueryChangeEffect] Processing query params:", JSON.stringify(currentQueryParams, null, 2));

			const matchedFilters = matchQueryWithValueList(
				currentQueryParams,
				filtersAvailableData,
			);

			// console.log("✨ [routerQueryChangeEffect] Matched filters:", JSON.stringify(matchedFilters, null, 2));

			// Force new references to ensure React detects changes
			matchedFilters.forEach((filter) => {
				queryFilters.push(JSON.parse(JSON.stringify(filter)));
			});
		}

		if (queryFilters.length > 0) {
			// console.log("🎯 [routerQueryChangeEffect] Applying query filters:", queryFilters.map(f => ({ id: f.id, values: f.values })));

			// Remove existing filters of same types and add new ones
			let newFiltersSelected = [...filterState.filtros];

			queryFilters.forEach((queryFilter) => {
				// Remove existing filter of same type
				newFiltersSelected = newFiltersSelected.filter(
					(f) => f.id !== queryFilter.id,
				);
				// Add new filter
				newFiltersSelected.push(queryFilter);
			});

			// console.log("🔄 [routerQueryChangeEffect] Setting new filters:", newFiltersSelected.map(f => ({ id: f.id, values: f.values })));
			setFiltersSelectedFromSystem(newFiltersSelected);
		}
	}, [filtersAvailableData, filterState.filtros, setFiltersSelectedFromSystem]);

	const hardUrlSegmentsToFollowChangesEffet = useCallback(() => {
		if (componentMounted.current === false) return;
		setHardUrlSegmentToFollow([tof]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tof]);

	const queryParsedEffect = useCallback(() => {
		const urlQuery = windowUrlQuery.current;
		const componentIsNotMountedYet = componentMounted.current === false;

		if (
			queryParsed.current === true ||
			isEmpty(urlQuery) ||
			filtersAvailableData === undefined ||
			filtersAvailableData.length === 0 ||
			componentIsNotMountedYet
		) {
			if (isEmpty(urlQuery) && queryParsed.current === false) {
				queryParsed.current = true;
				setFromPushToFalse();
				// Change status from init to idle when no query parameters
				dispatch(setStatusToIdle());
			}
			return;
		}
		const matchedValues = matchQueryWithValueList(
			urlQuery,
			filtersAvailableData,
		);

		// Set queryParsed immediately - no need for timeout with semantic URL checking
		queryParsed.current = true;
		if (
			matchedValues === undefined ||
			matchedValues === null ||
			matchedValues.length === 0
		)
			return setFromPushToFalse;
		const removeDupped = matchedValues.filter(
			(m) => filterState.filtros.find((f) => f.id === m.id) === undefined,
		);

		if (removeDupped.length === 0) return setFromPushToFalse();
		setFiltersSelectedFromSystem([
			...filterState.filtros,
			...(removeDupped as IFiltros[]),
		]);
		return setFromPushToFalse();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filtersAvailableData, setFromPushToFalse, setFiltersSelectedFromSystem]);

	const urlUpdateByFilterChangesEffect = useCallback(() => {
		// console.log(
		// 	'🔄 [urlUpdateByFilterChangesEffect] Starting:',
		// 	JSON.stringify(
		// 		{
		// 			filtersSelected: filtersSelected.map((f) => ({
		// 				id: f.id,
		// 				values: f.values,
		// 			})),
		// 			fromRouterPush: fromRouterPush.current,
		// 			componentMounted: componentMounted.current,
		// 			routerAsPath: router.asPath,
		// 		},
		// 		null,
		// 		2,
		// 	),
		// );

		// Only block if we're in the middle of a router push operation
		if (fromRouterPush.current === true) {
			// console.log(
			// 	'⏭️ [urlUpdateByFilterChangesEffect] Skipping - fromRouterPush',
			// );
			return;
		}

		// Don't block on queryParsed = false if component is mounted - user interactions should work
		if (!componentMounted.current) {
			// console.log('⏭️ [urlUpdateByFilterChangesEffect] Skipping - not mounted');
			return;
		}

		if (queryParsed.current === true && componentMounted.current)
			fromRouterPush.current = false;

		const [url, params_formated] =
			constructUrlFromFilters(filtersSelected, urlSlug) ?? [];

		if (url === undefined) {
			// console.warn('Unable to extract url from filters');
			return;
		}

		// console.log(
		// 	'🆕 [urlUpdateByFilterChangesEffect] Constructed URL:',
		// 	JSON.stringify(
		// 		{
		// 			url,
		// 			params_formated,
		// 			currentPath: router.asPath,
		// 		},
		// 		null,
		// 		2,
		// 	),
		// );

		const [firstSegment] = hardUrlSegmentToFollow;
		const [newParameterLessUrl] = url.split('?');
		const [, filter_tof] = newParameterLessUrl.split('/');
		const hasTrailingSlash =
			newParameterLessUrl.lastIndexOf('/') === newParameterLessUrl.length - 1;
		const currentUrl =
			'/' +
			(pathListaSeo as string[]).join('/') +
			(hasTrailingSlash ? '/' : '');

		// Skip if is the same path (and has no parameters)
		if (
			currentUrl === '/' &&
			params_formated === '' &&
			currentUrl === newParameterLessUrl
		) {
			// console.log("✅ [urlUpdateByFilterChangesEffect] URLs match, no push needed");
			return;
		}

		// Compare directly with router.asPath - simpler and more reliable
		const currentRouterUrl = router.asPath;
		const targetUrl = url;

		// Skip if the URLs are identical
		if (currentRouterUrl === targetUrl) {
			// console.log(
			// 	'✅ [urlUpdateByFilterChangesEffect] URLs identical, no push needed',
			// );
			return;
		}

		// Skip URL changes ONLY until user has actually interacted with filters
		// This prevents changing the URL from tracking parameters like ?o= on initial load
		// but allows URL changes once user starts interacting with filters
		if (isFirstLoad.current === true && !userHasInteracted.current) {
			// console.log(
			// 	'✅ [urlUpdateByFilterChangesEffect] No user interaction yet, maintaining original URL',
			// );
			return; // Don't update URL until user interacts
		}

		// Mark first load as complete once user has interacted
		if (userHasInteracted.current && isFirstLoad.current === true) {
			// console.log(
			// 	'🎯 [urlUpdateByFilterChangesEffect] User has interacted, marking first load complete',
			// );
			isFirstLoad.current = false;
		}

		// console.log(
		// 	'🚀 [urlUpdateByFilterChangesEffect] URL differs, pushing:',
		// 	JSON.stringify(
		// 		{
		// 			from: router.asPath,
		// 			to: url,
		// 		},
		// 		null,
		// 		2,
		// 	),
		// );

		//TODO: Fix full route.push
		const canBeShallow = firstSegment === filter_tof;

		// ALWAYS set fromRouterPush to prevent infinite loops
		fromRouterPush.current = true;

		if (canBeShallow === false) {
			// componentMounted.current = false;
			queryParsed.current = false;
		}
		if (canBeShallow === false && triggerSearch.current !== undefined) {
			clearTimeout(triggerSearch.current);
		}

		router.push(url, undefined, { shallow: canBeShallow }).then(() => {
			// Update windowUrlQuery immediately after successful router push
			windowUrlQuery.current = getUrlParameters();
			// Reset the flag immediately - semantic checking prevents infinite loops
			fromRouterPush.current = false;
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filtersSelected, urlSlug, router.asPath]);

	const filterSelectedChangesEffect = useCallback(() => {
		// console.log('🔄 [filterSelectedChangesEffect] Filters changed:', 
		// 	filtersSelected.map(f => ({ id: f.id, values: f.values }))
		// );
		dispatch(setFilter({ filtros: filtersSelected }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, filtersSelected]);

	// Detect user interactions with filters after initial load
	const userInteractionDetectionEffect = useCallback(() => {
		// console.log("🔍 [userInteractionDetectionEffect] Called:", JSON.stringify({
		// 	componentMounted: componentMounted.current,
		// 	queryParsed: queryParsed.current,
		// 	isFirstLoad: isFirstLoad.current,
		// 	userHasInteracted: userHasInteracted.current,
		// 	filtersCount: filtersSelected.length
		// }, null, 2));

		// Only start detecting after component is mounted and query is parsed
		if (!componentMounted.current || !queryParsed.current) {
			// console.log("⏭️ [userInteractionDetectionEffect] Skipping - not ready");
			return;
		}

		// If we're still in first load state and filters have changed, it means user interacted
		if (isFirstLoad.current === true && !userHasInteracted.current) {
			// console.log("⚠️ [userInteractionDetectionEffect] POTENTIALLY PROBLEMATIC - Setting timeout for user interaction detection");
			// Small delay to distinguish between automatic parsing and user interaction
			const timeoutId = setTimeout(() => {
				if (isFirstLoad.current === true) {
					// console.log("🎯 [userInteractionDetectionEffect] TIMEOUT FIRED - Marking as user interaction (this might cause duplicate search)");
					userHasInteracted.current = true;
				}
			}, 100); // Very short delay to catch user interactions

			return () => {
				// console.log("🧹 [userInteractionDetectionEffect] Cleaning up timeout");
				clearTimeout(timeoutId);
			};
		}
	}, [filtersSelected]);

	useEffect(componentMountEffect, [componentMountEffect]);
	useEffect(hardUrlSegmentsToFollowChangesEffet, [
		hardUrlSegmentsToFollowChangesEffet,
	]);
	useEffect(queryParsedEffect, [queryParsedEffect]);
	useEffect(urlUpdateByFilterChangesEffect, [urlUpdateByFilterChangesEffect]);
	useEffect(filterSelectedChangesEffect, [filterSelectedChangesEffect]);
	useEffect(routerQueryChangeEffect, [routerQueryChangeEffect, router.asPath]);
	useEffect(userInteractionDetectionEffect, [userInteractionDetectionEffect]);

	// Filter visible filters based on dependencies
	const visibleFilters = filterVisibleFilters(filtersAvailableData, filtersSelected) as IFiltros[];
	const routerPath = router.asPath;
	const pathList = routerPath.startsWith("/") ? routerPath.slice(1) : routerPath;

	return (
		<Filtros
			filtros={visibleFilters}
			setFiltros={setFiltersAvailableData as any}
			useFilterDependency={() => [
				filterDataState,
				(filterId, value) =>
					new Promise((res) => {
						dispatch(filterDataGet({ filterId, value, pathList: pathList }));
						res(undefined);
					}),
			]}
			isMobile={isMobile}
			filtersSelected={filtersSelected}
			setFiltersSelected={setFiltersSelectedWithCleaning}
			idsRequeridos={idsRequeridos}
			filtrosBase={filtrosBase}
			{...dataAttr}
		/>
	);
};
